import { createFileRoute } from "@tanstack/react-router";
import { useContext, useMemo } from "react";
import { MakeRouteMatchUnion } from "@tanstack/react-router";
import { TFunction } from "i18next";
import { useQueries } from "@tanstack/react-query";
import { AppError, Loader } from "@bfc-software/pallet";
import { AuthContext, AuthUtils } from "@bfc-software/app-toolbox";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import {
  TRANSLATION_NAMESPACE,
  useTranslation,
} from "@bfc-software/bfc-web-ui/i18n";

import { PlanSimulator } from "./-components/plan-simulator";
import { buildSimModel } from "./-components/plan-simulator/simulator-model";
import styles from "./index.module.css";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/task-plans/$task-plan/simulate/",
)({
  component: RouteComponent,

  staticData: {
    crumb: (match: MakeRouteMatchUnion, t: TFunction) => {
      const params = match.params as {
        lang: string;
        org: string;
        location: string;
        "task-plan": string;
        task: string;
      };
      const base = {
        lang: params.lang,
        org: params.org,
        location: params.location,
      };

      return [
        {
          label: t("page.taskPlans.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/task-plans",
          params: base,
        },
        {
          label: t("page.planSimulation.detailCrumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan",
          params: { ...base, "task-plan": params["task-plan"] },
        },
        {
          label: t("page.planSimulation.crumb"),
        },
      ];
    },
  },
});

function RouteComponent() {
  const { org, location, lang, "task-plan": planId } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const client = useMemo(() => apiClient(), []);

  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const tenantId = AuthUtils.getTenantId(authState, orgId, location);

  const planQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}",
    { params: { path: { orgId, id: planId } } },
    { enabled: !!tenantId },
  );

  const tasksQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/tasks",
    { params: { path: { orgId, id: planId } } },
    { enabled: !!tenantId },
  );

  const tasks = tasksQuery.data?.tasks ?? [];

  const detailQueries = useQueries({
    queries: tasks.map((task) =>
      client.queryOptions(
        "get",
        "/{orgId}/task-plans/{id}/tasks/{taskId}/details",
        {
          params: {
            path: { orgId, id: planId, taskId: task.task_id },
          },
        },
        { enabled: !!tenantId },
      ),
    ),
  });

  // Requirements are synthesized during activation, so there is nothing to fetch
  // until the plan has one. Unlike the pick details, these are best-effort: the
  // simulation is still meaningful without them, so they neither gate loading
  // nor fail the page.
  const activationId = planQuery.data?.current_activation_id ?? undefined;

  const requirementQueries = useQueries({
    queries: tasks.map((task) =>
      client.queryOptions(
        "get",
        "/{orgId}/task-plans/{id}/tasks/{taskId}/requirements",
        {
          params: {
            path: { orgId, id: planId, taskId: task.task_id },
          },
        },
        { enabled: !!tenantId && !!activationId },
      ),
    ),
  });

  const baseLoading = planQuery.isPending || tasksQuery.isPending;
  const detailsLoading =
    tasksQuery.isSuccess && detailQueries.some((q) => q.isPending);
  const isError =
    planQuery.isError ||
    tasksQuery.isError ||
    detailQueries.some((q) => q.isError);

  const plan = planQuery.data;
  const taskList = tasksQuery.data?.tasks;
  const detailsReady =
    tasksQuery.isSuccess && detailQueries.every((q) => q.isSuccess);

  const model = useMemo(() => {
    if (!plan || !taskList || !detailsReady) return null;
    const picks = detailQueries.flatMap((q) => q.data?.task_detail_picks ?? []);
    const requirements = requirementQueries.flatMap(
      (q) => q.data?.requirements ?? [],
    );
    return buildSimModel({ plan, tasks: taskList, picks, requirements });
  }, [plan, taskList, detailsReady, detailQueries, requirementQueries]);

  if (baseLoading || detailsLoading) {
    return (
      <div data-test-role="plan-simulator-loading">
        <Loader type="page" />
      </div>
    );
  }

  if (isError || !model) {
    return (
      <div data-test-role="plan-simulator-failed">
        <AppError
          description={t("page.planSimulator.failed")}
          resolution={t("page.planSimulator.failureSupport")}
        />
      </div>
    );
  }

  return (
    <div
      data-org-id={orgId}
      data-tenant-id={tenantId}
      className={styles.simulateRoute}
    >
      <PlanSimulator model={model} />
    </div>
  );
}
